/**
 * Whether.Market Custom Icon System
 * 
 * Consistent, scalable SVG icons designed for dark-theme trading UI.
 * All icons use currentColor for fill/stroke, so they inherit text color.
 * Default size: 24x24, adjustable via size prop.
 */

import React from 'react';

interface IconProps {
  size?: number;
  className?: string;
  color?: string;
  strokeWidth?: number;
}

const defaults = { size: 24, strokeWidth: 1.5 };

// ─── Weather Icons ────────────────────────────────────────

export const SunIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="4" />
    <line x1="12" y1="2" x2="12" y2="5" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <line x1="4.22" y1="4.22" x2="6.34" y2="6.34" />
    <line x1="17.66" y1="17.66" x2="19.78" y2="19.78" />
    <line x1="2" y1="12" x2="5" y2="12" />
    <line x1="19" y1="12" x2="22" y2="12" />
    <line x1="4.22" y1="19.78" x2="6.34" y2="17.66" />
    <line x1="17.66" y1="6.34" x2="19.78" y2="4.22" />
  </svg>
);

export const CloudIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
  </svg>
);

export const RainIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M16 13V4a4 4 0 0 0-8 0v1H6a4 4 0 0 0 0 8h10a3 3 0 0 0 0-6z" />
    <line x1="8" y1="16" x2="7" y2="20" />
    <line x1="12" y1="16" x2="11" y2="20" />
    <line x1="16" y1="16" x2="15" y2="20" />
  </svg>
);

export const SnowIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
    <line x1="8" y1="15" x2="8" y2="15.01" />
    <line x1="12" y1="17" x2="12" y2="17.01" />
    <line x1="16" y1="15" x2="16" y2="15.01" />
    <line x1="10" y1="19" x2="10" y2="19.01" />
    <line x1="14" y1="19" x2="14" y2="19.01" />
  </svg>
);

export const WindIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9.59 4.59A2 2 0 1 1 11 8H2" />
    <path d="M12.59 19.41A2 2 0 1 0 14 16H2" />
    <path d="M17.73 7.73A2.5 2.5 0 1 1 19.5 12H2" />
  </svg>
);

export const ThermometerIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
  </svg>
);

export const StormIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
    <polyline points="13 11 9 17 15 17 11 23" />
  </svg>
);

export const HumidityIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
  </svg>
);

// ─── Trading / UI Icons ────────────────────────────────────

export const ChartIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

export const BarChartIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

export const CandlestickIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="6" y1="2" x2="6" y2="6" />
    <rect x="4" y="6" width="4" height="8" rx="0.5" />
    <line x1="6" y1="14" x2="6" y2="18" />
    <line x1="12" y1="4" x2="12" y2="8" />
    <rect x="10" y="8" width="4" height="6" rx="0.5" fill={color} fillOpacity="0.3" />
    <line x1="12" y1="14" x2="12" y2="20" />
    <line x1="18" y1="3" x2="18" y2="7" />
    <rect x="16" y="7" width="4" height="10" rx="0.5" />
    <line x1="18" y1="17" x2="18" y2="22" />
  </svg>
);

export const TrendUpIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

export const TrendDownIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
    <polyline points="17 18 23 18 23 12" />
  </svg>
);

export const WalletIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="2" y="6" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
    <circle cx="17" cy="14" r="1.5" fill={color} />
  </svg>
);

export const GlobeIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

export const ProfileIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const UsersIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export const TrophyIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

export const TargetIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

export const CrystalBallIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="10" r="8" />
    <path d="M12 18v2" />
    <path d="M7 22h10" />
    <path d="M8 6c2 2 6 2 8 0" opacity="0.5" />
    <path d="M9 10c1.5 1.5 4.5 1.5 6 0" opacity="0.3" />
  </svg>
);

export const SparklineIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="2 17 7 12 12 15 17 8 22 11" />
  </svg>
);

export const CoinIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M14.5 9a3.5 3.5 0 0 0-5 0" />
    <path d="M9.5 15a3.5 3.5 0 0 0 5 0" />
    <line x1="12" y1="5" x2="12" y2="7" />
    <line x1="12" y1="17" x2="12" y2="19" />
  </svg>
);

export const LightningIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

export const SettingsIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

export const BookIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

export const MapPinIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export const BellIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

export const ShieldIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

export const StarIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export const LinkIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

export const RobotIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="4" y="8" width="16" height="12" rx="2" />
    <line x1="12" y1="4" x2="12" y2="8" />
    <circle cx="12" cy="4" r="1.5" fill={color} />
    <circle cx="9" cy="14" r="1.5" />
    <circle cx="15" cy="14" r="1.5" />
    <path d="M9 18h6" />
  </svg>
);

export const EarnIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <circle cx="12" cy="12" r="3" />
    <line x1="6" y1="12" x2="6" y2="12.01" />
    <line x1="18" y1="12" x2="18" y2="12.01" />
  </svg>
);

export const ReferralIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

export const JournalIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

export const CheckCircleIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

export const AlertIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

export const SearchIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

export const FilterIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

export const ArrowUpIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="12" y1="19" x2="12" y2="5" />
    <polyline points="5 12 12 5 19 12" />
  </svg>
);

export const ArrowDownIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <polyline points="19 12 12 19 5 12" />
  </svg>
);

export const CloseIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const MenuIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

export const ExternalLinkIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

export const CopyIcon: React.FC<IconProps> = ({ size = defaults.size, className, color = 'currentColor', strokeWidth = defaults.strokeWidth }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

// ─── Re-export all as named map ────────────────────────────

export const Icons = {
  sun: SunIcon,
  cloud: CloudIcon,
  rain: RainIcon,
  snow: SnowIcon,
  wind: WindIcon,
  thermometer: ThermometerIcon,
  storm: StormIcon,
  humidity: HumidityIcon,
  lightning: LightningIcon,
  chart: ChartIcon,
  barChart: BarChartIcon,
  candlestick: CandlestickIcon,
  trendUp: TrendUpIcon,
  trendDown: TrendDownIcon,
  wallet: WalletIcon,
  globe: GlobeIcon,
  profile: ProfileIcon,
  users: UsersIcon,
  trophy: TrophyIcon,
  target: TargetIcon,
  crystalBall: CrystalBallIcon,
  sparkline: SparklineIcon,
  coin: CoinIcon,
  settings: SettingsIcon,
  book: BookIcon,
  mapPin: MapPinIcon,
  bell: BellIcon,
  shield: ShieldIcon,
  star: StarIcon,
  link: LinkIcon,
  robot: RobotIcon,
  earn: EarnIcon,
  referral: ReferralIcon,
  journal: JournalIcon,
  checkCircle: CheckCircleIcon,
  alert: AlertIcon,
  search: SearchIcon,
  filter: FilterIcon,
  arrowUp: ArrowUpIcon,
  arrowDown: ArrowDownIcon,
  close: CloseIcon,
  menu: MenuIcon,
  externalLink: ExternalLinkIcon,
  copy: CopyIcon,
} as const;

export type IconName = keyof typeof Icons;
